import { Box, Typography } from "@mui/material";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { ROLL_GRADIENT_ID } from "../defs/RollGradient";
import type { VisualizerStand } from "../models/MillVisualizer";
import { buildBottomRollPath, buildTopRollPath } from "../utils/buildRollPath";
import { statusToBorder, statusToFill } from "../utils/statusToBorder";
import {
	PASS_LINE_Y,
	STAND_BLOCK_HEIGHT,
	STAND_BLOCK_WIDTH,
	STAND_BOTTOM_Y,
	STAND_TOP_Y,
	standCenterX,
} from "../utils/viewBoxLayout";
import { VisualizerTooltip } from "./VisualizerTooltip";

interface Props {
	stand: VisualizerStand;
	onClick?: (id: string) => void;
}

const ROLL_W = 46;
const ROLL_H = 50;
const PINCH_HALF_GAP = 6;
const TOP_ROLL_CY = PASS_LINE_Y - PINCH_HALF_GAP - ROLL_H / 2;
const BOTTOM_ROLL_CY = PASS_LINE_Y + PINCH_HALF_GAP + ROLL_H / 2;

const AXLE_OVERHANG = 4;
const AXLE_CAP_HALF_W = 2.5;
const AXLE_CAP_HALF_H = 3;

const fmt = (n: number | null | undefined, dp = 1): string =>
	n == null || !Number.isFinite(n) ? "—" : n.toFixed(dp);

const StandTooltipContent = ({ stand }: { stand: VisualizerStand }) => {
	const statusColor = statusToBorder(stand.status);
	return (
		<Box sx={{ minWidth: 180, fontSize: "0.75rem" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
					pb: 0.5,
					mb: 0.5,
					borderBottom: `1px solid ${mesfColors.grey[200]}`,
				}}
			>
				<Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
					{stand.name}
				</Typography>
				<Box
					sx={{
						width: 8,
						height: 8,
						borderRadius: "50%",
						bgcolor: statusColor,
					}}
				/>
				<Typography
					sx={{
						fontSize: "0.7rem",
						textTransform: "uppercase",
						color: statusColor,
						fontWeight: 600,
						letterSpacing: "0.04em",
					}}
				>
					{stand.status}
				</Typography>
			</Box>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
				<TooltipRow label="Speed" value={`${fmt(stand.speedMps)} m/s`} />
				<TooltipRow label="Gap" value={`${fmt(stand.gapMm, 2)} mm`} />
			</Box>
		</Box>
	);
};

const TooltipRow = ({ label, value }: { label: string; value: string }) => (
	<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
		<Typography
			sx={{
				fontSize: "0.7rem",
				color: mesfColors.text.secondary,
				textTransform: "uppercase",
				letterSpacing: "0.03em",
			}}
		>
			{label}
		</Typography>
		<Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
			{value}
		</Typography>
	</Box>
);

const renderAxle = (cx: number, cy: number, x: number) => (
	<>
		<line
			x1={x - AXLE_OVERHANG}
			x2={x + STAND_BLOCK_WIDTH + AXLE_OVERHANG}
			y1={cy}
			y2={cy}
			stroke={mesfColors.text.primary}
			strokeWidth={1.4}
		/>
		<rect
			x={x - AXLE_OVERHANG - AXLE_CAP_HALF_W}
			y={cy - AXLE_CAP_HALF_H}
			width={AXLE_CAP_HALF_W * 2}
			height={AXLE_CAP_HALF_H * 2}
			fill={mesfColors.text.primary}
			rx={0.5}
		/>
		<rect
			x={x + STAND_BLOCK_WIDTH + AXLE_OVERHANG - AXLE_CAP_HALF_W}
			y={cy - AXLE_CAP_HALF_H}
			width={AXLE_CAP_HALF_W * 2}
			height={AXLE_CAP_HALF_H * 2}
			fill={mesfColors.text.primary}
			rx={0.5}
		/>
		<circle cx={cx} cy={cy} r={1.8} fill={mesfColors.text.primary} />
	</>
);

export const StandBlock = ({ stand, onClick }: Props) => {
	const cx = standCenterX(stand.position);
	const x = cx - STAND_BLOCK_WIDTH / 2;
	const borderColor = statusToBorder(stand.status);
	const fillColor = statusToFill(stand.status);
	const handleClick = onClick ? () => onClick(stand.id) : undefined;

	const topRollD = buildTopRollPath(
		{ cx, cy: TOP_ROLL_CY, width: ROLL_W, height: ROLL_H },
		stand.expectedSection,
	);
	const bottomRollD = buildBottomRollPath(
		{ cx, cy: BOTTOM_ROLL_CY, width: ROLL_W, height: ROLL_H },
		stand.expectedSection,
	);

	return (
		<VisualizerTooltip title={<StandTooltipContent stand={stand} />}>
			<g
				onClick={handleClick}
				style={{ cursor: handleClick ? "pointer" : undefined }}
			>
				<text
					x={cx}
					y={STAND_TOP_Y - 5}
					textAnchor="middle"
					fontSize="11"
					fontWeight="700"
					fill={mesfColors.text.primary}
				>
					{stand.name}
				</text>

				<rect
					x={x}
					y={STAND_TOP_Y}
					width={STAND_BLOCK_WIDTH}
					height={STAND_BLOCK_HEIGHT}
					rx={4}
					ry={4}
					fill={fillColor}
					stroke={borderColor}
					strokeWidth={2}
				/>

				{stand.isCBT ? (
					<text
						x={cx}
						y={PASS_LINE_Y + 4}
						textAnchor="middle"
						fontSize="10"
						fill={mesfColors.text.secondary}
						fontStyle="italic"
					>
						cooling bed
					</text>
				) : (
					<>
						<path
							d={topRollD}
							fill={`url(#${ROLL_GRADIENT_ID})`}
							stroke={mesfColors.text.primary}
							strokeWidth={1}
						/>
						{renderAxle(cx, TOP_ROLL_CY, x)}

						<path
							d={bottomRollD}
							fill={`url(#${ROLL_GRADIENT_ID})`}
							stroke={mesfColors.text.primary}
							strokeWidth={1}
						/>
						{renderAxle(cx, BOTTOM_ROLL_CY, x)}
					</>
				)}

				<circle cx={cx} cy={STAND_BOTTOM_Y + 8} r={3.5} fill={borderColor} />
			</g>
		</VisualizerTooltip>
	);
};
